import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Pagination, Dialog, DialogTitle, 
  DialogContent, DialogActions, Alert, IconButton, useTheme, useMediaQuery, Card, Chip
} from '@mui/material';
import { Restore, DeleteForever } from '@mui/icons-material';
import api from '../services/api';
import { formatToIST } from '../utils/dateUtils';

const DeletedSamples = () => {
  const user = useSelector(state => state.auth.user);
  const { showAllDates } = useSelector(state => state.settings);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', sample: null });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user?.role === 'Admin') {
      fetchDeletedSamples();
    }
  }, [user, page]);

  const fetchDeletedSamples = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/samples/deleted/list?page=${page}`);
      setSamples(response.data.samples);
      setPagination({
        totalPages: response.data.totalPages,
        currentPage: response.data.currentPage,
        total: response.data.total
      });
    } catch (error) {
      setError('Failed to fetch deleted samples');
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (sampleId) => {
    try {
      await api.post(`/samples/${sampleId}/revert`);
      setSuccess('Sample reverted successfully');
      fetchDeletedSamples();
      setConfirmDialog({ open: false, action: '', sample: null });
      // Trigger refresh of active samples when switching back
      window.dispatchEvent(new CustomEvent('refreshActiveSamples'));
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to revert sample');
    }
  };

  const handlePermanentDelete = async (sampleId) => {
    try {
      await api.delete(`/samples/${sampleId}/permanent`);
      setSuccess('Sample permanently deleted');
      fetchDeletedSamples();
      setConfirmDialog({ open: false, action: '', sample: null });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete sample');
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  if (user?.role !== 'Admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Only Admin can access deleted samples</Alert>
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {samples.map((sample) => (
          <Card key={sample._id} sx={{ mb: 1.5, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {sample.labId}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setConfirmDialog({ 
                    open: true, 
                    action: 'revert', 
                    sample 
                  })}
                  title="Revert Sample"
                >
                  <Restore fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setConfirmDialog({ 
                    open: true, 
                    action: 'delete', 
                    sample 
                  })}
                  title="Permanently Delete"
                >
                  <DeleteForever fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {sample.sampleName}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {sample.departments.map(dept => (
                <Chip key={dept} label={dept} size="small" variant="outlined" />
              ))}
            </Box>
            {showAllDates && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Created: {formatToIST(sample.createdDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Deleted: {sample.history.find(h => h.eventType === 'soft_deleted') 
                    ? formatToIST(sample.history.find(h => h.eventType === 'soft_deleted').at)
                    : 'N/A'}
                </Typography>
              </Box>
            )}
          </Card>
        ))}
      </Box>

      {/* Desktop Table View */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lab ID</TableCell>
              <TableCell>Sample Name</TableCell>
              <TableCell>Departments</TableCell>
              {showAllDates && <TableCell>Created Date</TableCell>}
              {showAllDates && <TableCell>Deleted Date</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {samples.map((sample) => (
              <TableRow key={sample._id}>
                <TableCell>{sample.labId}</TableCell>
                <TableCell>{sample.sampleName}</TableCell>
                <TableCell>{sample.departments.join(', ')}</TableCell>
                {showAllDates && <TableCell>{formatToIST(sample.createdDate)}</TableCell>}
                {showAllDates && (
                  <TableCell>
                    {sample.history.find(h => h.eventType === 'soft_deleted') 
                      ? formatToIST(sample.history.find(h => h.eventType === 'soft_deleted').at)
                      : 'N/A'}
                  </TableCell>
                )}
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setConfirmDialog({ 
                      open: true, 
                      action: 'revert', 
                      sample 
                    })}
                    title="Revert Sample"
                  >
                    <Restore />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDialog({ 
                      open: true, 
                      action: 'delete', 
                      sample 
                    })}
                    title="Permanently Delete"
                  >
                    <DeleteForever />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={pagination.totalPages}
          page={pagination.currentPage}
          onChange={handlePageChange}
        />
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: '', sample: null })}>
        <DialogTitle>
          {confirmDialog.action === 'revert' ? 'Revert Sample' : 'Permanently Delete Sample'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'revert' 
              ? `Are you sure you want to revert sample "${confirmDialog.sample?.labId}"? It will be moved back to active samples.`
              : `Are you sure you want to permanently delete sample "${confirmDialog.sample?.labId}"? This action cannot be undone.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', sample: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.action === 'revert') {
                handleRevert(confirmDialog.sample._id);
              } else {
                handlePermanentDelete(confirmDialog.sample._id);
              }
            }}
            color={confirmDialog.action === 'revert' ? 'primary' : 'error'}
            variant="contained"
          >
            {confirmDialog.action === 'revert' ? 'Revert' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeletedSamples;